import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-scroll';
import { Phone, Clock, MapPin, Calendar, User, ChevronDown, Star, Check, Instagram, Send, Map, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { services, team, gallery, reviews, clinicInfo, doctorCases } from '../data/mock';
import BookingModal from '../components/BookingModal';
import DoctorBookingFlow from '../components/DoctorBookingFlow';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/home.css';

const Home = () => {
  const { isDark } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isDoctorBookingOpen, setIsDoctorBookingOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedGalleryFilter, setSelectedGalleryFilter] = useState('all');
  const [isBlurActive, setIsBlurActive] = useState(false);
  const [galleryTransition, setGalleryTransition] = useState(false);
  const [logoSrc, setLogoSrc] = useState('/img/chorny thema.png');
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [showCasesModal, setShowCasesModal] = useState(false);
  const [selectedDoctorCases, setSelectedDoctorCases] = useState(null);
  const [isClosingCases, setIsClosingCases] = useState(false);
  const [selectedDoctorForBooking, setSelectedDoctorForBooking] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const [selectedCase, setSelectedCase] = useState(null);
  const [currentCaseImageIndex, setCurrentCaseImageIndex] = useState(0);
  const [showCaseViewer, setShowCaseViewer] = useState(false);
  const [isClosingCaseViewer, setIsClosingCaseViewer] = useState(false);
  const [caseImageTransition, setCaseImageTransition] = useState(false);
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [showSubServicesModal, setShowSubServicesModal] = useState(false);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(null);
  const [isClosingSubServices, setIsClosingSubServices] = useState(false);
  const [casesTransition, setCasesTransition] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [showGalleryViewer, setShowGalleryViewer] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [isClosingGalleryViewer, setIsClosingGalleryViewer] = useState(false);

  // Memoize close handlers to prevent re-renders
  const handleBookingClose = useCallback(() => {
    setIsBookingOpen(false);
    setSelectedService(null);
    setSelectedDoctorForBooking(null);
  }, []);

  const handleDoctorBookingClose = useCallback(() => {
    console.log('🔴 Closing DoctorBookingFlow');
    setIsDoctorBookingOpen(false);
    setSelectedDoctor(null);
  }, []);

  // Gallery viewer handlers
  const handleGalleryImageClick = useCallback((image) => {
    setSelectedGalleryImage(image);
    setShowGalleryViewer(true);
  }, []);

  const closeGalleryViewer = useCallback(() => {
    setIsClosingGalleryViewer(true);
    setTimeout(() => {
      setShowGalleryViewer(false);
      setSelectedGalleryImage(null);
      setIsClosingGalleryViewer(false);
    }, 300);
  }, []);



  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle Escape key for gallery viewer
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showGalleryViewer) {
        closeGalleryViewer();
      }
    };
    
    if (showGalleryViewer) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showGalleryViewer, closeGalleryViewer]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add blur effect after component mounts with a smoother transition
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBlurActive(true);
    }, 300); // Slightly longer delay for smoother appearance
    
    return () => clearTimeout(timer);
  }, []);

  // Update logo when theme changes
  useEffect(() => {
    console.log('Theme changed:', isDark ? 'dark' : 'light');
    setLogoSrc(isDark ? '/img/chorny thema.png' : '/img/bely thema.png');
  }, [isDark]);

  // Handle logo click to scroll to hero section
  const handleLogoClick = () => {
    const heroSection = document.getElementById('home');
    if (heroSection) {
      heroSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle service group click
  const handleServiceGroupClick = (service) => {
    setSelectedServiceGroup(service);
    setShowSubServicesModal(true);
  };

  // Handle sub-service selection
  const handleSubServiceSelect = (subService) => {
    setSelectedService(subService.title);
    setShowSubServicesModal(false);
    setIsBookingOpen(true);
  };

  // Close sub-services modal
  const closeSubServicesModal = () => {
    setIsClosingSubServices(true);
    setTimeout(() => {
      setShowSubServicesModal(false);
      setSelectedServiceGroup(null);
      setIsClosingSubServices(false);
    }, 300);
  };

  // Update document title
  useEffect(() => {
    document.title = 'Crystal Dental Studio - Професійна стоматологія преміум-класу';
  }, []);

  const handleGalleryFilterChange = useCallback((filter) => {
    setGalleryTransition(true);
    setTimeout(() => {
      setSelectedGalleryFilter(filter);
      setGalleryTransition(false);
    }, 300);
  }, []);

  const filteredGallery = useMemo(() => 
    selectedGalleryFilter === 'all' 
      ? gallery 
      : gallery.filter(item => item.category === selectedGalleryFilter),
    [selectedGalleryFilter]
  );

  const visibleGallery = useMemo(() => {
    const galleryPerPage = 8;
    return filteredGallery.slice(currentGalleryIndex, currentGalleryIndex + galleryPerPage);
  }, [filteredGallery, currentGalleryIndex]);

  const handleNextGallery = useCallback(() => {
    if (currentGalleryIndex + 8 < filteredGallery.length) {
      setCurrentGalleryIndex(prev => prev + 8);
    }
  }, [currentGalleryIndex, filteredGallery.length]);

  const handlePrevGallery = useCallback(() => {
    if (currentGalleryIndex > 0) {
      setCurrentGalleryIndex(prev => Math.max(0, prev - 8));
    }
  }, [currentGalleryIndex]);

  // Reset gallery index when filter changes
  useEffect(() => {
    setCurrentGalleryIndex(0);
  }, [selectedGalleryFilter]);

  const visibleReviews = useMemo(() => {
    const reviewsPerPage = 6;
    return reviews.slice(currentReviewIndex, currentReviewIndex + reviewsPerPage);
  }, [currentReviewIndex]);

  const handleNextReviews = useCallback(() => {
    if (currentReviewIndex + 6 < reviews.length) {
      const reviewsGrid = document.querySelector('.reviews-grid');
      if (reviewsGrid) {
        reviewsGrid.style.animation = 'none';
        setTimeout(() => {
          setCurrentReviewIndex(prev => prev + 6);
          reviewsGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
        }, 10);
      }
    }
  }, [currentReviewIndex]);

  const handlePrevReviews = useCallback(() => {
    if (currentReviewIndex > 0) {
      const reviewsGrid = document.querySelector('.reviews-grid');
      if (reviewsGrid) {
        reviewsGrid.style.animation = 'none';
        setTimeout(() => {
          setCurrentReviewIndex(prev => Math.max(0, prev - 6));
          reviewsGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
        }, 10);
      }
    }
  }, [currentReviewIndex]);

  const visibleTeam = useMemo(() => {
    const teamPerPage = isMobile ? 1 : 3;
    return team.slice(currentTeamIndex, currentTeamIndex + teamPerPage);
  }, [currentTeamIndex, isMobile]);

  const handleNextTeam = useCallback(() => {
    const step = isMobile ? 1 : 3;
    if (currentTeamIndex + step < team.length) {
      const teamGrid = document.querySelector('.team-grid');
      if (teamGrid) {
        teamGrid.style.animation = 'none';
        setTimeout(() => {
          setCurrentTeamIndex(prev => prev + step);
          teamGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
        }, 10);
      }
    }
  }, [currentTeamIndex, isMobile]);

  const handlePrevTeam = useCallback(() => {
    if (currentTeamIndex > 0) {
      const step = isMobile ? 1 : 3;
      const teamGrid = document.querySelector('.team-grid');
      if (teamGrid) {
        teamGrid.style.animation = 'none';
        setTimeout(() => {
          setCurrentTeamIndex(prev => Math.max(0, prev - step));
          teamGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
        }, 10);
      }
    }
  }, [currentTeamIndex, isMobile]);

  return (
    <div className="home-container">
      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className={`header-content ${isMenuOpen ? 'menu-open' : ''}`}>
          <div className="logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img 
              key={logoSrc}
              src={logoSrc}
              alt="Crystal Dental Studio" 
              className="logo-image"
            />
          </div>
          
          <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
            <Link to="home" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Головна</Link>
            <Link to="about" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Про нас</Link>
            <Link to="services" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Послуги</Link>
            <Link to="team" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Команда</Link>
            <Link to="gallery" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Галерея</Link>
            <Link to="reviews" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Відгуки</Link>
            <Link to="contacts" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Контакти</Link>
            <Link to="military" smooth={true} duration={500} onClick={() => setIsMenuOpen(false)}>Для ЗСУ</Link>
          </nav>

          <div className="header-actions">
            <ThemeToggle />
            <button className="booking-btn" onClick={() => setIsBookingOpen(true)}>
              <Calendar size={18} />
              Записатися
            </button>
          </div>

          <div className="mobile-actions">
            <ThemeToggle />
            <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <div className={`hamburger ${isMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-background">
          <div className="hero-overlay"></div>
          <img 
            src="/img/prewiew.png" 
            alt="Modern Dental Clinic"
            className="hero-bg-image"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="title-line">Де кожна усмішка</span>
            <span className="title-line highlight">стає витвором мистецтва</span>
          </h1>
          <p className="hero-subtitle">
            Професійна стоматологія преміум-класу з використанням найсучасніших технологій
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => setIsBookingOpen(true)}>
              <Calendar size={20} />
              Записатися на прийом
            </button>
            <Link to="services" smooth={true} duration={500}>
              <button className={`btn-secondary ${isBlurActive ? 'blur-active' : ''}`}>
                Наші послуги
                <ChevronDown size={20} />
              </button>
            </Link>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <ChevronDown size={32} className="scroll-icon" />
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="about">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2 className="section-title">Про Crystal Dental Studio</h2>
              <div className="section-subtitle">Ваша посмішка — наша місія</div>
              <p className="about-text">
                Crystal Dental Studio — це сучасна стоматологічна клініка преміум-класу, 
                де поєднуються передові технології, професіоналізм та індивідуальний підхід 
                до кожного пацієнта.
              </p>
              <p className="about-text">
                Наша команда — це висококваліфіковані лікарі з багаторічним досвідом, 
                які постійно підвищують свою кваліфікацію на міжнародних конференціях 
                та навчаннях. Ми використовуємо тільки сертифіковані матеріали та 
                найсучасніше обладнання провідних світових виробників.
              </p>
              <div className="about-features">
                <div className="feature-item">
                  <div className="feature-icon"><Check size={24} /></div>
                  <div>
                    <h4>Безпека та стерильність</h4>
                    <p>Використання одноразових інструментів та багаторівнева стерилізація</p>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon"><Check size={24} /></div>
                  <div>
                    <h4>Сучасне обладнання</h4>
                    <p>Передові технології діагностики та лікування</p>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon"><Check size={24} /></div>
                  <div>
                    <h4>Безболісне лікування</h4>
                    <p>Сучасна анестезія та комфортні умови</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-images">
              {/* Временно скрыто главное изображение - для возврата раскомментировать */}
              {/* <div className="about-image-wrapper main-image">
                <img 
                  src="/img/2.jpg" 
                  alt="Consultation"
                  loading="lazy"
                  decoding="async"
                />
              </div> */}
              <div className="about-image-wrapper secondary-image-enlarged">
                <img 
                  src="/img/1.jpg" 
                  alt="Modern Equipment"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="container">
          <h2 className="section-title">Наші послуги</h2>
          <div className="section-subtitle">Комплексний підхід до вашого здоров'я</div>
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card" data-service-id={service.id}>
                <div className="service-image-wrapper">
                  <img src={service.image} alt={service.title} loading="lazy" decoding="async" />
                  <div className="service-overlay">
                    <button className="service-btn" onClick={() => handleServiceGroupClick(service)}>
                      Переглянути послуги
                    </button>
                  </div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section" id="team">
        <div className="container">
          <h2 className="section-title">Наша команда</h2>
          <div className="section-subtitle">Професіонали своєї справи</div>
          <div className="team-carousel-container">
            <button 
              className="team-nav-btn team-nav-prev" 
              onClick={handlePrevTeam}
              disabled={currentTeamIndex === 0}
              aria-label="Попередні лікарі"
            >
              <ChevronLeft size={28} />
            </button>
            <div className="team-grid">
              {visibleTeam.map((member) => (
                <div key={member.id} className="team-card" data-doctor-id={member.id}>
                  <div className="team-image-wrapper">
                    <img src={member.image} alt={member.name} loading="lazy" decoding="async" />
                    <div className="team-overlay">
                      <div className="team-buttons">
                        <button className="team-btn team-btn-primary" onClick={() => {
                          setSelectedDoctor(member);
                          setIsDoctorBookingOpen(true);
                        }}>
                          Записатися до лікаря
                        </button>
                        <button className="team-btn team-btn-secondary" onClick={() => {
                          setSelectedDoctorCases(member);
                          setShowCasesModal(true);
                        }}>
                          Подивитися кейси
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="team-content">
                    <h3>{member.name}</h3>
                    <p className="team-position">{member.position}</p>
                    <p className="team-experience">{member.experience}</p>
                    <p className="team-specialization">{member.specialization}</p>
                  </div>
                </div>
              ))}
            </div>
            <button 
              className="team-nav-btn team-nav-next" 
              onClick={handleNextTeam}
              disabled={currentTeamIndex + (isMobile ? 1 : 3) >= team.length}
              aria-label="Наступні лікарі"
            >
              <ChevronRight size={28} />
            </button>
          </div>
          <div className="team-pagination">
            {Array.from({ length: Math.ceil(team.length / (isMobile ? 1 : 3)) }).map((_, index) => (
              <button
                key={index}
                className={`pagination-dot ${currentTeamIndex / (isMobile ? 1 : 3) === index ? 'active' : ''}`}
                onClick={() => {
                  const teamGrid = document.querySelector('.team-grid');
                  if (teamGrid) {
                    teamGrid.style.animation = 'none';
                    setTimeout(() => {
                      setCurrentTeamIndex(index * (isMobile ? 1 : 3));
                      teamGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
                    }, 10);
                  }
                }}
                aria-label={`Сторінка ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section" id="gallery">
        <div className="container">
          <h2 className="section-title">Галерея</h2>
          <div className="section-subtitle">Наші роботи та клініка</div>
          <div className="gallery-filters">
            <button 
              className={selectedGalleryFilter === 'all' ? 'active' : ''}
              onClick={() => handleGalleryFilterChange('all')}
            >
              Всі
            </button>
            <button 
              className={selectedGalleryFilter === 'results' ? 'active' : ''}
              onClick={() => handleGalleryFilterChange('results')}
            >
              Результати
            </button>
            <button 
              className={selectedGalleryFilter === 'clinic' ? 'active' : ''}
              onClick={() => handleGalleryFilterChange('clinic')}
            >
              Клініка
            </button>
          </div>
          <div className="gallery-wrapper">
            {filteredGallery.length > 8 && !isMobile && (
              <>
                <button 
                  className="gallery-arrow gallery-arrow-left" 
                  onClick={handlePrevGallery}
                  disabled={currentGalleryIndex === 0}
                  aria-label="Попередні фото"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  className="gallery-arrow gallery-arrow-right" 
                  onClick={handleNextGallery}
                  disabled={currentGalleryIndex + 8 >= filteredGallery.length}
                  aria-label="Наступні фото"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            <div className={`gallery-grid ${galleryTransition ? 'gallery-transitioning' : ''}`}>
              {visibleGallery.map((item) => (
                <div 
                  key={item.id} 
                  className="gallery-item" 
                  data-gallery-id={item.id}
                  onClick={() => handleGalleryImageClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                  <div className="gallery-overlay">
                    <p>{item.title}</p>
                  </div>
                </div>
              ))}
            </div>
            {filteredGallery.length > 8 && isMobile && (
              <div className="gallery-arrows-mobile">
                <button 
                  className="gallery-arrow gallery-arrow-left" 
                  onClick={handlePrevGallery}
                  disabled={currentGalleryIndex === 0}
                  aria-label="Попередні фото"
                >
                  <ChevronLeft size={28} />
                </button>
                <button 
                  className="gallery-arrow gallery-arrow-right" 
                  onClick={handleNextGallery}
                  disabled={currentGalleryIndex + 8 >= filteredGallery.length}
                  aria-label="Наступні фото"
                >
                  <ChevronRight size={28} />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-section" id="reviews">
        <div className="container">
          <h2 className="section-title">Відгуки наших пацієнтів</h2>
          <div className="section-subtitle">Довіра та задоволення</div>
          <div className="reviews-carousel-container">
            <button 
              className="reviews-nav-btn reviews-nav-prev" 
              onClick={handlePrevReviews}
              disabled={currentReviewIndex === 0}
              aria-label="Попередні відгуки"
            >
              <ChevronLeft size={28} />
            </button>
            <div className="reviews-grid">
              {visibleReviews.map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="review-avatar">
                      <User size={24} />
                    </div>
                    <div>
                      <h4>{review.name}</h4>
                      <div className="review-rating">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={16} fill="currentColor" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="review-text">{review.text}</p>
                  <p className="review-date">{review.date}</p>
                </div>
              ))}
            </div>
            <button 
              className="reviews-nav-btn reviews-nav-next" 
              onClick={handleNextReviews}
              disabled={currentReviewIndex + 6 >= reviews.length}
              aria-label="Наступні відгуки"
            >
              <ChevronRight size={28} />
            </button>
          </div>
          <div className="reviews-pagination">
            {Array.from({ length: Math.ceil(reviews.length / 6) }).map((_, index) => (
              <button
                key={index}
                className={`pagination-dot ${currentReviewIndex / 6 === index ? 'active' : ''}`}
                onClick={() => {
                  const reviewsGrid = document.querySelector('.reviews-grid');
                  if (reviewsGrid) {
                    reviewsGrid.style.animation = 'none';
                    setTimeout(() => {
                      setCurrentReviewIndex(index * 6);
                      reviewsGrid.style.animation = 'fadeInReviews 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
                    }, 10);
                  }
                }}
                aria-label={`Сторінка ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contacts Section */}
      <section className="contacts-section" id="contacts">
        <div className="container">
          <h2 className="section-title">Контакти</h2>
          <div className="section-subtitle">Ми завжди раді вас бачити</div>
          <div className="contacts-grid">
            <div className="contacts-info">
              <div className="contact-item">
                <MapPin size={24} />
                <div>
                  <h4>Адреса</h4>
                  <p>{clinicInfo.address}</p>
                </div>
              </div>
              <div className="contact-item">
                <Phone size={24} />
                <div>
                  <h4>Телефон</h4>
                  <p>{clinicInfo.phone}</p>
                </div>
              </div>

              <div className="contact-item">
                <Clock size={24} />
                <div>
                  <h4>Години роботи</h4>
                  <p>Пн-Пт: {clinicInfo.workingHours.weekdays}</p>
                  <p>Сб: {clinicInfo.workingHours.saturday}</p>
                  <p>Нд: {clinicInfo.workingHours.sunday}</p>
                </div>
              </div>
            </div>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2539.6964866194884!2d30.630698!3d50.468843!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40d4c5c6e6c6c6c6%3A0x0!2z0LLRg9C70LjRhtGPINCu0YDRltGPINCG0LvQu9GU0L3QutCwLCAxONCRLCDQmtC40ZfQsiwgMDQxMTk!5e0!3m2!1suk!2sua!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Crystal Dental Studio Location"
              ></iframe>
            </div>
          </div>
          <div className="social-links">
            <a href="https://www.instagram.com/crystal_dental_studio_by_vyzhu" target="_blank" rel="noopener noreferrer" className="social-btn">
              <Instagram size={20} />
              <span>Instagram</span>
            </a>
            <a href="https://t.me/crystal_dental" target="_blank" rel="noopener noreferrer" className="social-btn">
              <Send size={20} />
              <span>Telegram</span>
            </a>
            <a href="https://maps.app.goo.gl/JzZzC6yzCBK2Cd6X6" target="_blank" rel="noopener noreferrer" className="social-btn">
              <Map size={20} />
              <span>Google Maps</span>
            </a>
          </div>
        </div>
      </section>

      {/* Military Discount Section */}
      <section className="military-section" id="military">
        <div className="container">
          <h2 className="section-title">Для захисників України</h2>
          <div className="section-subtitle">Подяка нашим героям за мир і свободу</div>
          <div className="military-benefits">
            <div className="benefit-card">
              <div className="benefit-icon-wrapper">
                <Check className="benefit-icon" size={48} />
              </div>
              <h3>Безкоштовне видалення</h3>
              <p>Професійне видалення зубів без оплати</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-wrapper">
                <Star className="benefit-icon" size={48} />
              </div>
              <h3>Знижка 30%</h3>
              <p>На лікування, імплантацію, протезування та інші послуги</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-wrapper">
                <Sparkles className="benefit-icon" size={48} />
              </div>
              <h3>Знижка 15%</h3>
              <p>Спеціальна ціна на естетичну реставрацію вінірами</p>
            </div>
          </div>
          <div className="military-note">
            <p>
              Знижки надаються при пред'явленні військового квитка або посвідчення учасника бойових дій
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img 
                key={logoSrc + '-footer'}
                src={logoSrc}
                alt="Crystal Dental Studio" 
                className="footer-logo"
              />
              <p>Професійна стоматологія преміум-класу</p>
            </div>
            <div className="footer-section">
              <h4>Навігація</h4>
              <Link to="home" smooth={true} duration={500}>Головна</Link>
              <Link to="about" smooth={true} duration={500}>Про нас</Link>
              <Link to="services" smooth={true} duration={500}>Послуги</Link>
              <Link to="team" smooth={true} duration={500}>Команда</Link>
            </div>
            <div className="footer-section">
              <h4>Контакти</h4>
              <p>{clinicInfo.phone}</p>
              <p>{clinicInfo.email}</p>
              <p>{clinicInfo.address}</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Crystal Dental Studio. Всі права захищені.</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {isBookingOpen && (
        <BookingModal 
          onClose={handleBookingClose} 
          preSelectedService={selectedService}
          preSelectedDoctor={selectedDoctorForBooking}
        />
      )}

      {/* Doctor Booking Flow */}
      {isDoctorBookingOpen && selectedDoctor && (
        <DoctorBookingFlow 
          doctor={selectedDoctor}
          onClose={handleDoctorBookingClose}
          isOpen={isDoctorBookingOpen}
        />
      )}

      {/* Doctor Cases Modal */}
      {showCasesModal && selectedDoctorCases && (
        <div 
          className={`cases-modal-overlay ${isClosingCases ? 'closing' : ''}`} 
          onClick={() => {
            setIsClosingCases(true);
            setTimeout(() => {
              setShowCasesModal(false);
              setIsClosingCases(false);
            }, 300);
          }}
        >
          <div className={`cases-modal ${isClosingCases ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="cases-modal-header">
              <h3>Кейси лікаря {selectedDoctorCases.name}</h3>
              <button 
                className="cases-modal-close" 
                onClick={() => {
                  setIsClosingCases(true);
                  setTimeout(() => {
                    setShowCasesModal(false);
                    setIsClosingCases(false);
                  }, 300);
                }}
              >
                ✕
              </button>
            </div>
            <div className="cases-modal-body">
              {doctorCases[selectedDoctorCases.id] && doctorCases[selectedDoctorCases.id].length > 0 ? (
                <div className="cases-carousel-wrapper">
                  {doctorCases[selectedDoctorCases.id].length > 2 && (
                    <>
                      <button 
                        className="cases-nav-arrow cases-nav-arrow-left"
                        onClick={() => {
                          if (currentCaseIndex > 0) {
                            setCasesTransition(true);
                            setTimeout(() => {
                              if (isMobile) {
                                setCurrentCaseIndex(prev => prev - 1);
                              } else {
                                const totalCases = doctorCases[selectedDoctorCases.id].length;
                                const isOdd = totalCases % 2 !== 0;
                                if (isOdd && currentCaseIndex === totalCases - 1) {
                                  setCurrentCaseIndex(currentCaseIndex - 2);
                                } else {
                                  setCurrentCaseIndex(prev => Math.max(0, prev - 2));
                                }
                              }
                              setCasesTransition(false);
                            }, 300);
                          }
                        }}
                        disabled={currentCaseIndex === 0}
                        aria-label="Попередній кейс"
                      >
                        <ChevronLeft size={28} />
                      </button>
                      <button 
                        className="cases-nav-arrow cases-nav-arrow-right"
                        onClick={() => {
                          const totalCases = doctorCases[selectedDoctorCases.id].length;
                          
                          if (isMobile) {
                            if (currentCaseIndex < totalCases - 1) {
                              setCasesTransition(true);
                              setTimeout(() => {
                                setCurrentCaseIndex(prev => prev + 1);
                                setCasesTransition(false);
                              }, 300);
                            }
                          } else {
                            const isOdd = totalCases % 2 !== 0;
                            const maxIndex = isOdd ? totalCases - 1 : totalCases - 2;
                            
                            if (currentCaseIndex < maxIndex) {
                              setCasesTransition(true);
                              setTimeout(() => {
                                if (isOdd && currentCaseIndex + 2 >= totalCases) {
                                  setCurrentCaseIndex(totalCases - 1);
                                } else {
                                  setCurrentCaseIndex(prev => prev + 2);
                                }
                                setCasesTransition(false);
                              }, 300);
                            }
                          }
                        }}
                        disabled={(() => {
                          const totalCases = doctorCases[selectedDoctorCases.id].length;
                          if (isMobile) {
                            return currentCaseIndex >= totalCases - 1;
                          }
                          const isOdd = totalCases % 2 !== 0;
                          return isOdd ? currentCaseIndex >= totalCases - 1 : currentCaseIndex >= totalCases - 2;
                        })()}
                        aria-label="Наступний кейс"
                      >
                        <ChevronRight size={28} />
                      </button>
                    </>
                  )}
                  <div className={`cases-grid ${casesTransition ? 'transitioning' : ''} ${(() => {
                    const totalCases = doctorCases[selectedDoctorCases.id].length;
                    const isLastOdd = totalCases % 2 !== 0 && currentCaseIndex === totalCases - 1;
                    return isLastOdd || isMobile ? 'single-case' : '';
                  })()}`}>
                    {(() => {
                      const totalCases = doctorCases[selectedDoctorCases.id].length;
                      const isLastOdd = totalCases % 2 !== 0 && currentCaseIndex === totalCases - 1;
                      const casesToShow = isMobile ? 1 : (isLastOdd ? 1 : 2);
                      return doctorCases[selectedDoctorCases.id].slice(currentCaseIndex, currentCaseIndex + casesToShow);
                    })().map((caseItem) => (
                      <div 
                        key={caseItem.id} 
                        className="case-card"
                      >
                        <div className="case-image-wrapper">
                          <img src={caseItem.preview} alt={caseItem.title} loading="lazy" decoding="async" />
                          <div className="case-overlay">
                            <button 
                              className="case-btn"
                              onClick={() => {
                                setSelectedCase(caseItem);
                                setCurrentCaseImageIndex(0);
                                setShowCaseViewer(true);
                              }}
                            >
                              Подивитися
                            </button>
                          </div>
                        </div>
                        <div className="case-content">
                          <h4>{caseItem.title}</h4>
                          <p>{caseItem.description}</p>
                          <div className="case-info">
                            <span className="case-price">{caseItem.price}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-cases">
                  <p>У цього лікаря поки немає доступних кейсів</p>
                </div>
              )}
            </div>
            <div className="cases-modal-footer">
              <button 
                className="submit-btn doctor-action-btn" 
                onClick={() => {
                  setIsClosingCases(true);
                  setTimeout(() => {
                    const doctorName = `${selectedDoctorCases.name} (${selectedDoctorCases.position})`;
                    setShowCasesModal(false);
                    setIsClosingCases(false);
                    setSelectedDoctorForBooking(doctorName);
                    setIsBookingOpen(true);
                  }, 300);
                }}
              >
                <Calendar size={20} />
                Записатися до лікаря
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case Viewer Modal */}
      {showCaseViewer && selectedCase && (
        <div 
          className={`case-viewer-overlay ${isClosingCaseViewer ? 'closing' : ''}`}
          onClick={() => {
            setIsClosingCaseViewer(true);
            setTimeout(() => {
              setShowCaseViewer(false);
              setIsClosingCaseViewer(false);
            }, 300);
          }}
        >
          <div className={`case-viewer ${isClosingCaseViewer ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button 
              className="case-viewer-close" 
              onClick={() => {
                setIsClosingCaseViewer(true);
                setTimeout(() => {
                  setShowCaseViewer(false);
                  setIsClosingCaseViewer(false);
                }, 300);
              }}
            >
              ✕
            </button>
            
            <div className="case-viewer-content">
              <button 
                className="case-viewer-arrow case-viewer-arrow-left"
                onClick={() => {
                  setCaseImageTransition(true);
                  setTimeout(() => {
                    setCurrentCaseImageIndex(prev => 
                      prev > 0 ? prev - 1 : selectedCase.images.length - 1
                    );
                    setCaseImageTransition(false);
                  }, 150);
                }}
                aria-label="Попереднє фото"
              >
                <ChevronLeft size={32} />
              </button>

              <div className="case-viewer-image-container">
                <img 
                  key={currentCaseImageIndex}
                  src={selectedCase.images[currentCaseImageIndex]} 
                  alt={`${selectedCase.title} - фото ${currentCaseImageIndex + 1}`}
                  loading="lazy"
                  decoding="async"
                  className={caseImageTransition ? 'transitioning' : ''}
                />
                <div className="case-viewer-counter">
                  {currentCaseImageIndex + 1} / {selectedCase.images.length}
                </div>
              </div>

              <button 
                className="case-viewer-arrow case-viewer-arrow-right"
                onClick={() => {
                  setCaseImageTransition(true);
                  setTimeout(() => {
                    setCurrentCaseImageIndex(prev => 
                      prev < selectedCase.images.length - 1 ? prev + 1 : 0
                    );
                    setCaseImageTransition(false);
                  }, 150);
                }}
                aria-label="Наступне фото"
              >
                <ChevronRight size={32} />
              </button>
            </div>

            <div className="case-viewer-info">
              <h3>{selectedCase.title}</h3>
              <p>{selectedCase.description}</p>
              <span className="case-viewer-price">{selectedCase.price}</span>
            </div>
          </div>
        </div>
      )}
      {/* Sub-services Modal */}
      {showSubServicesModal && selectedServiceGroup && (
        <div className={`sub-services-modal-overlay ${isClosingSubServices ? 'closing' : ''}`} onClick={closeSubServicesModal}>
          <div className={`sub-services-modal ${isClosingSubServices ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="sub-services-modal-header">
              <h3>{selectedServiceGroup.title}</h3>
              <button className="sub-services-modal-close" onClick={closeSubServicesModal}>
                <X size={24} />
              </button>
            </div>
            <div className="sub-services-modal-content">
              <p className="sub-services-description">{selectedServiceGroup.description}</p>
              <div className="sub-services-list">
                {selectedServiceGroup.subServices?.map((subService) => (
                  <div key={subService.id} className="sub-service-modal-item">
                    <div className="sub-service-modal-info">
                      <h4>{subService.title}</h4>
                      <p>{subService.description}</p>
                      <div className="sub-service-modal-details">
                        <span className="sub-service-modal-duration">
                          <Clock size={16} />
                          {subService.duration}
                        </span>
                        <span className="sub-service-modal-price">{subService.price}</span>
                      </div>
                    </div>
                    <button 
                      className="sub-service-modal-btn"
                      onClick={() => handleSubServiceSelect(subService)}
                    >
                      Записатися
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Image Viewer */}
      {showGalleryViewer && selectedGalleryImage && (
        <div 
          className={`gallery-viewer-overlay ${isClosingGalleryViewer ? 'closing' : ''}`}
          onClick={closeGalleryViewer}
        >
          <div className="gallery-viewer-container">
            <button 
              className="gallery-viewer-close"
              onClick={closeGalleryViewer}
              aria-label="Закрыть"
            >
              <X size={24} />
            </button>
            <div 
              className="gallery-viewer-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedGalleryImage.image} 
                alt={selectedGalleryImage.title}
                className="gallery-viewer-image"
              />
              <div className="gallery-viewer-title">
                {selectedGalleryImage.title}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;